import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/Button';
import { Product, ProductModifierGroup } from '../types';
import { Image as ImageIcon, Plus, Trash2, Search, Edit2, X, Sparkles, Tags, Boxes, Wand2, ExternalLink, Loader2, PackageCheck, Truck, AlertTriangle, Barcode, DollarSign, Percent, Warehouse, Scale, CalendarDays, ShieldCheck, ClipboardList, PackagePlus, Check, ArrowLeft, CircleHelp } from 'lucide-react';
import { formatCurrency } from '../services/dataService';
import { api } from '../services/api';
import { CatalogProduct, GENERATED_PRE_CATALOG, catalogImageFor } from '../data/preCatalog';
import { SupplierPriceManager } from '../components/SupplierPriceManager';
import { SpotlightTour, SpotlightStep } from '../components/SpotlightTour';
import { useNavigate } from 'react-router-dom';
import { advanceFirstAccessTour, getActiveTourArea } from '../utils/firstAccessTour';

const PLACEHOLDER_IMAGE = 'https://placehold.co/400x300?text=Produto';
const CATALOG_IMAGE_CACHE_KEY = 'flowtab.catalog.images.v15';
const STOCK_UNIT_LABELS: Record<NonNullable<Product['stockUnit']>, string> = {
  UN: 'un.',
  G: 'g',
  KG: 'kg',
  ML: 'ml',
  L: 'L'
};

const formatStockQuantity = (quantity: unknown, unit: Product['stockUnit'] = 'UN') => {
  const numericQuantity = Number(quantity ?? 0);
  if (!Number.isFinite(numericQuantity)) return '0';
  const roundedQuantity = unit === 'UN'
    ? Math.round(numericQuantity)
    : Math.round((numericQuantity + Number.EPSILON) * 1000) / 1000;
  return roundedQuantity.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
};

const createEmptyProduct = (category = 'Lanches'): Partial<Product> => ({
  name: '',
  price: 0,
  category,
  description: '',
  image: '',
  barcode: '',
  sku: '',
  brand: '',
  ncm: '',
  cfop: '',
  location: '',
  purchasePrice: 0,
  costPrice: 0,
  profitMargin: 0,
  wholesaleQuantity: 0,
  wholesalePrice: 0,
  grossWeight: 0,
  netWeight: 0,
  expirationDate: '',
  active: true,
  allowSale: true,
  composition: false,
  manufactured: false,
  fractioned: false,
  weighable: false,
  trackStock: false,
  stockQuantity: 0,
  lowStockThreshold: 5,
  stockProductId: '',
  stockDeductQuantity: 1,
  stockIngredients: [],
  recipeYield: 1,
  preparationMethod: '',
  stockPackageProductId: '',
  stockPackageSize: 0,
  stockUnit: 'UN',
  internalUseOnly: false,
  saleUnit: 'UNIT',
  productionArea: 'AUTO',
  availableForDelivery: false
});

const FIELD_CLASS = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-100';
const LABEL_CLASS = 'mb-1.5 block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400';

const productTourSteps: SpotlightStep[] = [
  { target: 'products-tab', title: 'Seu catálogo de produtos', description: 'Aqui você organiza os itens vendidos no PDV. Fornecedores ficam em outra aba e não fazem parte deste guia.' },
  { target: 'products-categories', title: 'Organize por categorias', description: 'As categorias agrupam os produtos e ajudam a encontrá-los rapidamente no PDV. Toque em uma categoria para filtrar a lista.' },
  { target: 'products-add-category', title: 'Crie uma categoria', description: 'Use o botão + para cadastrar uma categoria própria. Também há um atalho Categoria ao lado de Novo Produto.' },
  { target: 'products-search', title: 'Encontre um produto', description: 'Busque pelo nome. O resultado também respeita a categoria selecionada na lateral.' },
  { target: 'products-new', title: 'Cadastre do zero', description: 'Novo Produto abre um formulário dividido em três áreas: dados, estoque e preços.' },
  { target: 'products-catalog', title: 'Ou parta de um modelo', description: 'Produtos pré-cadastrados permite escolher itens conhecidos e ajustar preço e estoque antes de adicioná-los.' },
  { target: 'products-card', title: 'Veja e gerencie cada item', description: 'O cartão mostra imagem, categoria, preço e indicadores. Use Editar para ajustar ou Excluir para remover.' },
  { target: 'products-editor-basic', title: 'Dados e imagem', description: 'Nome, preço de venda e categoria são os dados principais. Códigos, marca e localização são opcionais.' },
  { target: 'products-editor-image', title: 'Imagem do produto', description: 'Cole uma URL ou use Auto buscar. Uma imagem clara ajuda a reconhecer o item no catálogo.' },
  { target: 'products-editor-availability', title: 'Disponibilidade', description: 'Produto ativo controla o cadastro. Vender no PDV decide se ele pode ser lançado em uma comanda.' },
  { target: 'products-editor-stock', title: 'Estoque e canais', description: 'Nesta etapa você escolhe se o item é um insumo interno, se aparece no delivery e se terá controle de estoque.' },
  { target: 'products-editor-tracking', title: 'Controle quantidades', description: 'Ao ativar Controlar Estoque, você informa a quantidade, a unidade e o ponto de alerta. Também pode configurar abertura automática de embalagens.' },
  { target: 'products-editor-ingredients', title: 'Ficha técnica', description: 'Associe insumos consumidos em cada venda, como ingredientes de uma porção ou de um drink. Deixe vazio quando não precisar.' },
  { target: 'products-editor-pricing', title: 'Preços e operação', description: 'Compra, custo, margem e atacado são opcionais; preencha somente o que usar no seu negócio.' },
  { target: 'products-editor-operation', title: 'Destino e medidas', description: 'Defina para onde vai o preparo e, quando habilitado, se a venda é por unidade ou balança. Pesos e validade também ficam aqui.' },
  { target: 'products-editor-save', title: 'Pronto para cadastrar', description: 'Depois de revisar as três áreas, salve o produto. Durante este guia o botão fica desativado; nenhum exemplo é gravado.' }
];

type ProductEditorTab = 'basic' | 'stock' | 'advanced';

type CatalogDraft = { price: string; trackStock: boolean; stockQuantity: string };

const LEGACY_PRE_CATALOG: Omit<CatalogProduct, 'image'>[] = [
  { id: 'brahma-600', name: 'Brahma 600ml', category: 'Cervejas', brand: 'Brahma', stockUnit: 'UN' },
  { id: 'brahma-lata-350', name: 'Brahma lata 350ml', category: 'Cervejas', brand: 'Brahma', stockUnit: 'UN' },
  { id: 'brahma-duplo-malte-350', name: 'Brahma Duplo Malte 350ml', category: 'Cervejas', brand: 'Brahma', stockUnit: 'UN' },
  { id: 'skol-600', name: 'Skol 600ml', category: 'Cervejas', brand: 'Skol', stockUnit: 'UN' },
  { id: 'skol-lata-350', name: 'Skol lata 350ml', category: 'Cervejas', brand: 'Skol', stockUnit: 'UN' },
  { id: 'antarctica-600', name: 'Antarctica 600ml', category: 'Cervejas', brand: 'Antarctica', stockUnit: 'UN' },
  { id: 'antarctica-lata-350', name: 'Antarctica lata 350ml', category: 'Cervejas', brand: 'Antarctica', stockUnit: 'UN' },
  { id: 'heineken-600', name: 'Heineken 600ml', category: 'Cervejas', brand: 'Heineken', stockUnit: 'UN' },
  { id: 'heineken-long-neck', name: 'Heineken long neck 330ml', category: 'Cervejas', brand: 'Heineken', stockUnit: 'UN' },
  { id: 'heineken-lata-350', name: 'Heineken lata 350ml', category: 'Cervejas', brand: 'Heineken', stockUnit: 'UN' },
  { id: 'original-600', name: 'Original 600ml', category: 'Cervejas', brand: 'Original', stockUnit: 'UN' },
  { id: 'stella-600', name: 'Stella Artois 600ml', category: 'Cervejas', brand: 'Stella Artois', stockUnit: 'UN' },
  { id: 'stella-long-neck', name: 'Stella Artois long neck 330ml', category: 'Cervejas', brand: 'Stella Artois', stockUnit: 'UN' },
  { id: 'budweiser-600', name: 'Budweiser 600ml', category: 'Cervejas', brand: 'Budweiser', stockUnit: 'UN' },
  { id: 'budweiser-long-neck', name: 'Budweiser long neck 330ml', category: 'Cervejas', brand: 'Budweiser', stockUnit: 'UN' },
  { id: 'spaten-600', name: 'Spaten 600ml', category: 'Cervejas', brand: 'Spaten', stockUnit: 'UN' },
  { id: 'ambev-puro-malte-350', name: 'Amstel lata 350ml', category: 'Cervejas', brand: 'Amstel', stockUnit: 'UN' },
  { id: 'amstel-600', name: 'Amstel 600ml', category: 'Cervejas', brand: 'Amstel', stockUnit: 'UN' },
  { id: 'itaipava-600', name: 'Itaipava 600ml', category: 'Cervejas', brand: 'Itaipava', stockUnit: 'UN' },
  { id: 'petra-600', name: 'Petra 600ml', category: 'Cervejas', brand: 'Petra', stockUnit: 'UN' },
  { id: 'corona-long-neck', name: 'Corona Extra 330ml', category: 'Cervejas', brand: 'Corona', stockUnit: 'UN' },
  { id: 'beck-lata-350', name: "Beck's lata 350ml", category: 'Cervejas', brand: "Beck's", stockUnit: 'UN' },
  { id: 'brahma-zero-350', name: 'Brahma Zero 350ml', category: 'Cervejas sem álcool', brand: 'Brahma', stockUnit: 'UN' },
  { id: 'heineken-zero-long-neck', name: 'Heineken 0.0 long neck 330ml', category: 'Cervejas sem álcool', brand: 'Heineken', stockUnit: 'UN' },
  { id: 'coca-2l', name: 'Coca-Cola 2L', category: 'Refrigerantes', brand: 'Coca-Cola', stockUnit: 'UN' },
  { id: 'coca-350', name: 'Coca-Cola 350ml', category: 'Refrigerantes', brand: 'Coca-Cola', stockUnit: 'UN' },
  { id: 'coca-600', name: 'Coca-Cola 600ml', category: 'Refrigerantes', brand: 'Coca-Cola', stockUnit: 'UN' },
  { id: 'coca-zero-350', name: 'Coca-Cola Zero 350ml', category: 'Refrigerantes', brand: 'Coca-Cola', stockUnit: 'UN' },
  { id: 'coca-zero-2l', name: 'Coca-Cola Zero 2L', category: 'Refrigerantes', brand: 'Coca-Cola', stockUnit: 'UN' },
  { id: 'guarana-2l', name: 'Guaraná Antarctica 2L', category: 'Refrigerantes', brand: 'Antarctica', stockUnit: 'UN' },
  { id: 'guarana-350', name: 'Guaraná Antarctica 350ml', category: 'Refrigerantes', brand: 'Antarctica', stockUnit: 'UN' },
  { id: 'guarana-zero-350', name: 'Guaraná Antarctica Zero 350ml', category: 'Refrigerantes', brand: 'Antarctica', stockUnit: 'UN' },
  { id: 'sprite-2l', name: 'Sprite 2L', category: 'Refrigerantes', brand: 'Sprite', stockUnit: 'UN' },
  { id: 'sprite-350', name: 'Sprite 350ml', category: 'Refrigerantes', brand: 'Sprite', stockUnit: 'UN' },
  { id: 'fanta-2l', name: 'Fanta Laranja 2L', category: 'Refrigerantes', brand: 'Fanta', stockUnit: 'UN' },
  { id: 'fanta-laranja-350', name: 'Fanta Laranja 350ml', category: 'Refrigerantes', brand: 'Fanta', stockUnit: 'UN' },
  { id: 'fanta-uva-350', name: 'Fanta Uva 350ml', category: 'Refrigerantes', brand: 'Fanta', stockUnit: 'UN' },
  { id: 'pepsi-350', name: 'Pepsi 350ml', category: 'Refrigerantes', brand: 'Pepsi', stockUnit: 'UN' },
  { id: 'pepsi-2l', name: 'Pepsi 2L', category: 'Refrigerantes', brand: 'Pepsi', stockUnit: 'UN' },
  { id: 'soda-350', name: 'Soda limonada 350ml', category: 'Refrigerantes', stockUnit: 'UN' },
  { id: 'agua-500', name: 'Água mineral 500ml', category: 'Águas', stockUnit: 'UN' },
  { id: 'agua-gas-500', name: 'Água com gás 500ml', category: 'Águas', stockUnit: 'UN' },
  { id: 'agua-1-5l', name: 'Água mineral 1,5L', category: 'Águas', stockUnit: 'UN' },
  { id: 'agua-tonica-350', name: 'Água tônica 350ml', category: 'Águas', stockUnit: 'UN' },
  { id: 'agua-coco-1l', name: 'Água de coco 1L', category: 'Águas', stockUnit: 'UN' },
  { id: 'red-bull-250', name: 'Red Bull 250ml', category: 'Energéticos', brand: 'Red Bull', stockUnit: 'UN' },
  { id: 'monster-473', name: 'Monster Energy 473ml', category: 'Energéticos', brand: 'Monster', stockUnit: 'UN' },
  { id: 'fusion-473', name: 'Fusion Energy 473ml', category: 'Energéticos', brand: 'Fusion', stockUnit: 'UN' },
  { id: 'suco-laranja-300', name: 'Suco de laranja 300ml', category: 'Sucos', stockUnit: 'UN' },
  { id: 'suco-uva-300', name: 'Suco de uva 300ml', category: 'Sucos', stockUnit: 'UN' },
  { id: 'suco-maracuja-300', name: 'Suco de maracujá 300ml', category: 'Sucos', stockUnit: 'UN' },
  { id: 'suco-limao-300', name: 'Suco de limão 300ml', category: 'Sucos', stockUnit: 'UN' },
  { id: 'suco-polpa-500', name: 'Suco de polpa 500ml', category: 'Sucos', stockUnit: 'UN' },
  { id: 'dose-whisky-red', name: 'Dose Johnnie Walker Red Label', category: 'Doses', brand: 'Johnnie Walker', stockUnit: 'UN' },
  { id: 'dose-whisky-black', name: 'Dose Johnnie Walker Black Label', category: 'Doses', brand: 'Johnnie Walker', stockUnit: 'UN' },
  { id: 'dose-jack-daniels', name: "Dose Jack Daniel's", category: 'Doses', brand: "Jack Daniel's", stockUnit: 'UN' },
  { id: 'dose-white-horse', name: 'Dose White Horse', category: 'Doses', brand: 'White Horse', stockUnit: 'UN' },
  { id: 'dose-vodka-smirnoff', name: 'Dose Vodka Smirnoff', category: 'Doses', brand: 'Smirnoff', stockUnit: 'UN' },
  { id: 'dose-vodka-absolut', name: 'Dose Vodka Absolut', category: 'Doses', brand: 'Absolut', stockUnit: 'UN' },
  { id: 'dose-gin-tanqueray', name: 'Dose Gin Tanqueray', category: 'Doses', brand: 'Tanqueray', stockUnit: 'UN' },
  { id: 'dose-gin-beefeater', name: 'Dose Gin Beefeater', category: 'Doses', brand: 'Beefeater', stockUnit: 'UN' },
  { id: 'dose-cachaca-51', name: 'Dose Cachaça 51', category: 'Doses', brand: '51', stockUnit: 'UN' },
  { id: 'dose-cachaca-seleta', name: 'Dose Cachaça Seleta', category: 'Doses', brand: 'Seleta', stockUnit: 'UN' },
  { id: 'dose-tequila', name: 'Dose Tequila', category: 'Doses', stockUnit: 'UN' },
  { id: 'dose-campari', name: 'Dose Campari', category: 'Doses', brand: 'Campari', stockUnit: 'UN' },
  { id: 'dose-licor-43', name: 'Dose Licor 43', category: 'Doses', brand: 'Licor 43', stockUnit: 'UN' },
  { id: 'caipirinha-limao', name: 'Caipirinha de limão', category: 'Drinks', stockUnit: 'UN' },
  { id: 'caipiroska-limao', name: 'Caipiroska de limão', category: 'Drinks', stockUnit: 'UN' },
  { id: 'gin-tonica', name: 'Gin tônica', category: 'Drinks', stockUnit: 'UN' },
  { id: 'moscow-mule', name: 'Moscow Mule', category: 'Drinks', stockUnit: 'UN' },
  { id: 'cuba-libre', name: 'Cuba Libre', category: 'Drinks', stockUnit: 'UN' },
  { id: 'aperol-spritz', name: 'Aperol Spritz', category: 'Drinks', stockUnit: 'UN' },
  { id: 'porcao-batata-p', name: 'Porção de batata frita pequena', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-batata-g', name: 'Porção de batata frita grande', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-batata-cheddar-bacon', name: 'Batata com cheddar e bacon', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-calabresa', name: 'Porção de calabresa acebolada', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-frango-passaro', name: 'Porção de frango a passarinho', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-mandioca', name: 'Porção de mandioca frita', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-polenta', name: 'Porção de polenta frita', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-torresmo', name: 'Porção de torresmo', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-file-acebolado', name: 'Porção de filé acebolado', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-isca-frango', name: 'Porção de isca de frango', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-peixe', name: 'Porção de peixe frito', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-aneis-cebola', name: 'Porção de anéis de cebola', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-pastel', name: 'Porção de pastel', category: 'Porções', stockUnit: 'UN' },
  { id: 'porcao-frios', name: 'Tábua de frios', category: 'Porções', stockUnit: 'UN' },
  { id: 'coxinha', name: 'Coxinha', category: 'Salgados', stockUnit: 'UN' },
  { id: 'kibe', name: 'Kibe', category: 'Salgados', stockUnit: 'UN' },
  { id: 'empada', name: 'Empada', category: 'Salgados', stockUnit: 'UN' },
  { id: 'esfirra-carne', name: 'Esfirra de carne', category: 'Salgados', stockUnit: 'UN' },
  { id: 'pastel-carne', name: 'Pastel de carne', category: 'Salgados', stockUnit: 'UN' },
  { id: 'pastel-queijo', name: 'Pastel de queijo', category: 'Salgados', stockUnit: 'UN' },
  { id: 'pao-queijo', name: 'Pão de queijo', category: 'Salgados', stockUnit: 'UN' },
  { id: 'enroladinho-salsicha', name: 'Enroladinho de salsicha', category: 'Salgados', stockUnit: 'UN' },
  { id: 'hamburguer-simples', name: 'Hambúrguer simples', category: 'Lanches', stockUnit: 'UN' },
  { id: 'x-salada', name: 'X-Salada', category: 'Lanches', stockUnit: 'UN' },
  { id: 'x-bacon', name: 'X-Bacon', category: 'Lanches', stockUnit: 'UN' },
  { id: 'x-tudo', name: 'X-Tudo', category: 'Lanches', stockUnit: 'UN' },
  { id: 'misto-quente', name: 'Misto-quente', category: 'Lanches', stockUnit: 'UN' },
  { id: 'cachorro-quente', name: 'Cachorro-quente', category: 'Lanches', stockUnit: 'UN' },
  { id: 'bombom', name: 'Bombom', category: 'Doces', stockUnit: 'UN' },
  { id: 'chocolate-barra', name: 'Chocolate em barra', category: 'Doces', stockUnit: 'UN' },
  { id: 'pacoca', name: 'Paçoca', category: 'Doces', stockUnit: 'UN' },
  { id: 'pe-moleque', name: 'Pé de moleque', category: 'Doces', stockUnit: 'UN' },
  { id: 'doce-leite', name: 'Doce de leite', category: 'Doces', stockUnit: 'UN' },
  { id: 'brigadeiro', name: 'Brigadeiro', category: 'Doces', stockUnit: 'UN' },
  { id: 'pudim', name: 'Pudim', category: 'Sobremesas', stockUnit: 'UN' },
  { id: 'mousse-maracuja', name: 'Mousse de maracujá', category: 'Sobremesas', stockUnit: 'UN' },
  { id: 'sorvete-taca', name: 'Taça de sorvete', category: 'Sobremesas', stockUnit: 'UN' },
  { id: 'bolo-fatia', name: 'Fatia de bolo', category: 'Sobremesas', stockUnit: 'UN' },
  { id: 'amendoim', name: 'Amendoim', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'castanha', name: 'Castanha', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'salame', name: 'Salame', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'ovo-codorna', name: 'Ovo de codorna', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'azeitona', name: 'Azeitona', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'batata-chips', name: 'Batata chips', category: 'Petiscos', stockUnit: 'UN' },
  { id: 'gelo-5kg', name: 'Gelo 5kg', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'gelo-1kg', name: 'Gelo 1kg', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'carvao-3kg', name: 'Carvão 3kg', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'fosforo', name: 'Caixa de fósforos', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'isqueiro', name: 'Isqueiro', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'copo-descartavel', name: 'Copo descartável', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'trident', name: 'Trident', category: 'Conveniência', brand: 'Trident', stockUnit: 'UN' },
  { id: 'halls', name: 'Halls', category: 'Conveniência', brand: 'Halls', stockUnit: 'UN' },
  { id: 'bala', name: 'Bala', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'pirulito', name: 'Pirulito', category: 'Conveniência', stockUnit: 'UN' },
  { id: 'arroz-5kg', name: 'Arroz 5kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'arroz-1kg', name: 'Arroz 1kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'feijao-1kg', name: 'Feijão 1kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'acucar-1kg', name: 'Açúcar 1kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'oleo-900', name: 'Óleo de soja 900ml', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'leite-1l', name: 'Leite integral 1L', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'cafe-500g', name: 'Café 500g', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'sal-1kg', name: 'Sal 1kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'farinha-trigo-1kg', name: 'Farinha de trigo 1kg', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'macarrao-500g', name: 'Macarrão 500g', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'molho-tomate', name: 'Molho de tomate', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'biscoito-recheado', name: 'Biscoito recheado', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'biscoito-salgado', name: 'Biscoito salgado', category: 'Mercearia', stockUnit: 'UN' },
  { id: 'detergente', name: 'Detergente', category: 'Limpeza', stockUnit: 'UN' },
  { id: 'agua-sanitaria', name: 'Água sanitária', category: 'Limpeza', stockUnit: 'UN' },
  { id: 'desinfetante', name: 'Desinfetante', category: 'Limpeza', stockUnit: 'UN' },
  { id: 'papel-higienico', name: 'Papel higiênico', category: 'Higiene', stockUnit: 'UN' },
  { id: 'sabonete', name: 'Sabonete', category: 'Higiene', stockUnit: 'UN' },
  { id: 'creme-dental', name: 'Creme dental', category: 'Higiene', stockUnit: 'UN' }
];

const PRE_CATALOG: CatalogProduct[] = Array.from(new Map([
  ...GENERATED_PRE_CATALOG,
  ...LEGACY_PRE_CATALOG
    .filter(item => !/^dose (dupla )?de /i.test(item.name))
    .map(item => ({ ...item, image: catalogImageFor(item.name, item.category, item.brand) }))
].map(item => [item.name.toLocaleLowerCase('pt-BR'), item])).values());

export const MenuManager = () => {
  const navigate = useNavigate();
  const { user, products, categories, currentCompany, addProduct, updateProduct, deleteProduct, addCategory, deleteCategory } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProductTab, setActiveProductTab] = useState<'products' | 'suppliers'>('products');
  const [catalogImages, setCatalogImages] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(CATALOG_IMAGE_CACHE_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const catalogImageAttempts = useRef(new Set<string>());
  const catalogScrollRef = useRef<HTMLDivElement>(null);
  const [catalogImagesLoading, setCatalogImagesLoading] = useState<Set<string>>(new Set());
  const [catalogImagesFailed, setCatalogImagesFailed] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [productEditorTab, setProductEditorTab] = useState<ProductEditorTab>('basic');
  const productFormScrollRef = useRef<HTMLDivElement>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [newProduct, setNewProduct] = useState<Partial<Product>>(() => createEmptyProduct(categories[0] || 'Lanches'));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [catalogStep, setCatalogStep] = useState<1 | 2>(1);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('Todas');
  const [catalogVisibleLimit, setCatalogVisibleLimit] = useState(60);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  const [catalogDrafts, setCatalogDrafts] = useState<Record<string, CatalogDraft>>({});
  const [isCatalogSaving, setIsCatalogSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourStorageKey = `flowtab-products-tour-v1:${user?.id}`;
  const tourSnapshotRef = useRef<{
    isModalOpen: boolean;
    editingId: string | null;
    newProduct: Partial<Product>;
    productEditorTab: ProductEditorTab;
    searchTerm: string;
    selectedCategory: string;
  } | null>(null);
  const showTourExample = tourOpen && products.length === 0;
  const currentTourSteps = useMemo(() => productTourSteps.map(step => showTourExample && step.target === 'products-card'
    ? { ...step, detail: 'Este cartão aparece apenas no guia; nenhum produto de exemplo será salvo.' }
    : step), [showTourExample]);

  const startTour = useCallback(() => {
    tourSnapshotRef.current = { isModalOpen, editingId, newProduct, productEditorTab, searchTerm, selectedCategory };
    setSearchTerm('');
    setSelectedCategory('Todos');
    setIsModalOpen(false);
    setTourStep(0);
    setTourOpen(true);
  }, [isModalOpen, editingId, newProduct, productEditorTab, searchTerm, selectedCategory]);

  useEffect(() => {
    if (!user?.id || !currentCompany?.id || isModalOpen || isCatalogOpen || tourOpen || activeProductTab !== 'products') return;
    if (getActiveTourArea(user.id) === 'products') {
      startTour();
      return;
    }
    let active = true;
    const checkTour = async () => {
      const status = await api.auth.getTutorialStatus('products');
      if (!active) return;
      const seenLocally = window.localStorage.getItem(tourStorageKey) === 'seen';
      if (seenLocally && status && !status.completed) void api.auth.completeTutorial('products');
      if (seenLocally || status?.completed) {
        window.localStorage.setItem(tourStorageKey, 'seen');
        return;
      }
      startTour();
    };
    void checkTour();
    return () => { active = false; };
  }, [user?.id, currentCompany?.id, isModalOpen, isCatalogOpen, tourOpen, activeProductTab, tourStorageKey, startTour]);

  const changeTourStep = (index: number) => {
    const target = productTourSteps[index].target;
    if (target.startsWith('products-editor-')) {
      if (!isModalOpen) {
        const snapshot = tourSnapshotRef.current;
        if (snapshot?.isModalOpen) {
          setEditingId(snapshot.editingId);
          setNewProduct(snapshot.newProduct);
        } else if (products[0]) {
          handleOpenModal(products[0]);
        } else {
          setEditingId(null);
          setNewProduct({ ...createEmptyProduct('Exemplo'), name: 'Produto de exemplo', price: 12 });
        }
        setIsModalOpen(true);
      }
      if (index >= 11 && index <= 12) setNewProduct(previous => ({ ...previous, trackStock: true }));
      setProductEditorTab(index >= 13 ? 'advanced' : index >= 10 ? 'stock' : 'basic');
      productFormScrollRef.current?.scrollTo?.(0, 0);
    } else {
      setIsModalOpen(false);
    }
    setTourStep(index);
  };

  const finishTour = () => {
    window.localStorage.setItem(tourStorageKey, 'seen');
    void api.auth.completeTutorial('products');
    const nextRoute = user ? advanceFirstAccessTour(user, 'products') : null;
    const snapshot = tourSnapshotRef.current;
    if (snapshot) {
      setIsModalOpen(snapshot.isModalOpen);
      setEditingId(snapshot.editingId);
      setNewProduct(snapshot.newProduct);
      setProductEditorTab(snapshot.productEditorTab);
      setSearchTerm(snapshot.searchTerm);
      setSelectedCategory(snapshot.selectedCategory);
    } else {
      setIsModalOpen(false);
    }
    tourSnapshotRef.current = null;
    setTourOpen(false);
    setTourStep(0);
    if (nextRoute) navigate(nextRoute, { replace: true });
  };

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return products.filter(product => {
      const matchesSearch = !normalizedSearch || product.name.toLowerCase().includes(normalizedSearch);
      const matchesCategory = selectedCategory === 'Todos' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const categorySummaries = useMemo(() => {
    return categories.map(category => ({
      name: category,
      total: products.filter(product => product.category === category).length
    }));
  }, [categories, products]);

  const stockSourceProducts = useMemo(() => {
    return products
      .filter(product => product.trackStock && product.id !== editingId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, editingId]);

  const estimatedRecipeCost = useMemo(() => {
    const yieldQuantity = Math.max(0.001, Number(newProduct.recipeYield || 1));
    return (newProduct.stockIngredients || []).reduce((total, ingredient) => {
      const source = products.find(product => product.id === ingredient.productId);
      if (!source) return total;
      const from = ingredient.unit || source.stockUnit || 'UN';
      const to = source.stockUnit || 'UN';
      const quantity = Number(ingredient.quantity || 0);
      const converted = from === to ? quantity
        : from === 'G' && to === 'KG' ? quantity / 1000
          : from === 'KG' && to === 'G' ? quantity * 1000
            : from === 'ML' && to === 'L' ? quantity / 1000
              : from === 'L' && to === 'ML' ? quantity * 1000
                : 0;
      return total + converted * Number(source.costPrice || source.purchasePrice || 0);
    }, 0) / yieldQuantity;
  }, [newProduct.recipeYield, newProduct.stockIngredients, products]);

  const totalStockItems = products.filter(product => product.trackStock).length;
  const lowStockItems = products.filter(product => {
    const threshold = Math.max(0, Number(product.lowStockThreshold ?? 5));
    const quantity = Number(product.stockQuantity ?? 0);
    return product.trackStock && threshold > 0 && quantity > 0 && quantity <= threshold;
  }).length;
  const outOfStockItems = products.filter(product => product.trackStock && Number(product.stockQuantity ?? 0) <= 0).length;
  const stockAlertItems = lowStockItems + outOfStockItems;
  const deliveryItems = products.filter(product => product.availableForDelivery && product.internalUseOnly !== true).length;

  const normalizedProductNames = useMemo(
    () => new Set(products.map(product => product.name.trim().toLocaleLowerCase('pt-BR'))),
    [products]
  );
  const catalogCategories = useMemo(() => ['Todas', ...Array.from(new Set(PRE_CATALOG.map(item => item.category))).sort((a, b) => a.localeCompare(b, 'pt-BR'))], []);
  const filteredCatalogProducts = useMemo(() => {
    const term = catalogSearch.trim().toLocaleLowerCase('pt-BR');
    return PRE_CATALOG.filter(item => (catalogCategory === 'Todas' || item.category === catalogCategory) && (!term || `${item.name} ${item.category} ${item.brand || ''}`.toLocaleLowerCase('pt-BR').includes(term)));
  }, [catalogSearch, catalogCategory]);
  const visibleCatalogProducts = useMemo(() => filteredCatalogProducts.slice(0, catalogVisibleLimit), [filteredCatalogProducts, catalogVisibleLimit]);
  const catalogImage = (item: CatalogProduct) => {
    const cached = catalogImages[item.id];
    return cached || item.image || PLACEHOLDER_IMAGE;
  };

  const openCatalog = () => {
    catalogImageAttempts.current.clear();
    setCatalogStep(1);
    setCatalogSearch('');
    setCatalogCategory('Todas');
    setCatalogVisibleLimit(60);
    setSelectedCatalogIds([]);
    setCatalogDrafts({});
    setIsCatalogOpen(true);
  };

  const toggleCatalogProduct = (item: CatalogProduct) => {
    if (normalizedProductNames.has(item.name.trim().toLocaleLowerCase('pt-BR'))) return;
    setSelectedCatalogIds(current => current.includes(item.id) ? current.filter(id => id !== item.id) : [...current, item.id]);
  };

  const prepareCatalogDrafts = () => {
    if (selectedCatalogIds.length === 0) return;
    setCatalogDrafts(current => selectedCatalogIds.reduce<Record<string, CatalogDraft>>((drafts, id) => {
      drafts[id] = current[id] || { price: '', trackStock: true, stockQuantity: '0' };
      return drafts;
    }, {}));
    setCatalogStep(2);
  };

  const saveCatalogProducts = async () => {
    const selectedItems = PRE_CATALOG.filter(item => selectedCatalogIds.includes(item.id));
    const invalidDraft = selectedItems.some(item => {
      const draft = catalogDrafts[item.id];
      const price = Number(draft?.price.replace(',', '.'));
      const stockQuantity = Number(draft?.stockQuantity.replace(',', '.'));
      return !draft || draft.price.trim() === '' || !Number.isFinite(price) || price < 0 || (draft.trackStock && (!Number.isFinite(stockQuantity) || stockQuantity < 0));
    });
    if (invalidDraft) {
      alert('Preencha preços e quantidades válidos para continuar.');
      return;
    }

    setIsCatalogSaving(true);
    try {
      const missingCategories = Array.from(new Set(selectedItems.map(item => item.category).filter(category => !categories.includes(category))));
      for (const category of missingCategories) await addCategory(category);
      for (const item of selectedItems) {
        const draft = catalogDrafts[item.id];
        await addProduct({
          ...createEmptyProduct(item.category),
          id: crypto.randomUUID(),
          name: item.name,
          category: item.category,
          brand: item.brand || '',
          price: Number(draft.price.replace(',', '.')),
          image: catalogImage(item),
          trackStock: draft.trackStock,
          stockQuantity: draft.trackStock ? Number(draft.stockQuantity.replace(',', '.')) : 0,
          stockUnit: item.stockUnit || 'UN'
        } as Product);
      }
      setIsCatalogOpen(false);
    } catch (error) {
      console.error('Erro ao cadastrar produtos selecionados:', error);
      alert('Não foi possível concluir o cadastro. Tente novamente.');
    } finally {
      setIsCatalogSaving(false);
    }
  };

  const handleSaveCategory = async () => {
    if (newCategoryName && newCategoryName.trim() !== '') {
      await addCategory(newCategoryName.trim());
      setNewCategoryName('');
      setIsAddingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a categoria "${categoryName}"? Esta ação não pode ser desfeita.`)) {
      const result = await deleteCategory(categoryName);
      if (!result.success) alert(result.message);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    const confirmed = window.confirm(`Tem certeza que deseja excluir "${product.name}"?\n\nEssa ação remove o produto do cardápio e do estoque.`);
    if (!confirmed) return;
    await deleteProduct(product.id);
  };

  const searchCommercialProductImage = useCallback(async (query: string) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return null;
    try {
      const result = await api.products.findCatalogImage(normalizedQuery);
      return result?.imageUrl || null;
    } catch {
      return null;
    }
  }, []);

  const loadCatalogProductImage = useCallback(async (item: CatalogProduct, force = false) => {
    if (!force && (catalogImages[item.id] || item.image || catalogImagesLoading.has(item.id))) return;

    catalogImageAttempts.current.add(item.id);
    setCatalogImagesLoading(current => new Set(current).add(item.id));
    setCatalogImagesFailed(current => {
      const next = new Set(current);
      next.delete(item.id);
      return next;
    });

    const contextualQuery = `${item.name} ${item.brand || ''} produto original fundo branco`.trim();
    const imageUrl = await searchCommercialProductImage(contextualQuery)
      || await searchCommercialProductImage(item.name);
    if (imageUrl) {
      setCatalogImages(current => {
        const next = { ...current, [item.id]: imageUrl };
        localStorage.setItem(CATALOG_IMAGE_CACHE_KEY, JSON.stringify(next));
        return next;
      });
    } else {
      setCatalogImagesFailed(current => new Set(current).add(item.id));
    }

    setCatalogImagesLoading(current => {
      const next = new Set(current);
      next.delete(item.id);
      return next;
    });
  }, [catalogImages, catalogImagesLoading, searchCommercialProductImage]);

  useEffect(() => {
    if (catalogStep !== 1 || !isCatalogOpen || !catalogScrollRef.current) return;
    const scrollContainer = catalogScrollRef.current;
    const productsById = new Map(visibleCatalogProducts.map(item => [item.id, item]));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const productId = (entry.target as HTMLElement).dataset.catalogProductId;
        const item = productId ? productsById.get(productId) : undefined;
        if (item) loadCatalogProductImage(item);
        observer.unobserve(entry.target);
      });
    }, {
      root: scrollContainer,
      rootMargin: '120px 0px',
      threshold: 0.01
    });

    scrollContainer.querySelectorAll<HTMLElement>('[data-catalog-product-id]').forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, [catalogStep, isCatalogOpen, visibleCatalogProducts, loadCatalogProductImage]);

  const handleImageAutoSearch = async () => {
    if (!newProduct.name) {
      alert('Digite o nome do produto primeiro.');
      return;
    }

    setIsImageLoading(true);
    try {
      const query = `${newProduct.name} ${newProduct.category || ''}`.trim();
      const imageUrl = await searchCommercialProductImage(query) || await searchCommercialProductImage(newProduct.name || '');

      if (imageUrl) {
        setNewProduct(prev => ({ ...prev, image: imageUrl }));
      } else {
        alert('Não encontrei uma imagem automática confiável. Vou abrir o Google Imagens para você escolher uma URL manualmente.');
        window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(newProduct.name)}`, '_blank');
      }
    } catch (error) {
      alert('A busca automática falhou. Vou abrir o Google Imagens para você escolher manualmente.');
      window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(newProduct.name)}`, '_blank');
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleImageSearch = () => {
    if (!newProduct.name) {
      alert('Digite o nome do produto primeiro.');
      return;
    }
    window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(newProduct.name)}`, '_blank');
  };

  const handleOpenModal = (product?: Product) => {
    setProductEditorTab('basic');
    if (product) {
      setEditingId(product.id);
      setNewProduct({
        ...product,
        saleUnit: product.saleUnit || (product.weighable ? 'KG' : 'UNIT'),
        stockIngredients: product.stockIngredients?.length
          ? product.stockIngredients
          : product.stockProductId
            ? [{ productId: product.stockProductId, quantity: product.stockDeductQuantity || 1 }]
            : []
      });
    } else {
      setEditingId(null);
      setNewProduct(createEmptyProduct(categories[0] || 'Lanches'));
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (tourOpen) return;
    if (!newProduct.name || typeof newProduct.price !== 'number' || newProduct.price < 0) {
      setProductEditorTab('basic');
      alert('Por favor, preencha o nome e um preço válido (pode ser 0).');
      return;
    }

    const validStockIngredients = (newProduct.stockIngredients || [])
      .filter(ingredient => ingredient.productId && Number(ingredient.quantity) > 0)
      .map(ingredient => ({ productId: ingredient.productId, quantity: Math.max(0.001, Number(ingredient.quantity || 1)), unit: ingredient.unit || 'UN' }));

    const productData = {
      id: editingId || crypto.randomUUID(),
      name: newProduct.name,
      price: Number(newProduct.price),
      category: newProduct.category || 'Outros',
      description: newProduct.description || '',
      image: newProduct.image || PLACEHOLDER_IMAGE,
      barcode: newProduct.barcode || '',
      sku: newProduct.sku || '',
      brand: newProduct.brand || '',
      ncm: newProduct.ncm || '',
      cfop: newProduct.cfop || '',
      location: newProduct.location || '',
      purchasePrice: Number(newProduct.purchasePrice || 0),
      costPrice: Number(newProduct.costPrice || 0),
      profitMargin: Number(newProduct.profitMargin || 0),
      wholesaleQuantity: Number(newProduct.wholesaleQuantity || 0),
      wholesalePrice: Number(newProduct.wholesalePrice || 0),
      grossWeight: Number(newProduct.grossWeight || 0),
      netWeight: Number(newProduct.netWeight || 0),
      expirationDate: newProduct.expirationDate || '',
      active: newProduct.active !== false,
      allowSale: newProduct.allowSale !== false,
      composition: newProduct.composition === true,
      manufactured: newProduct.manufactured === true,
      fractioned: newProduct.fractioned === true,
      weighable: newProduct.saleUnit === 'KG',
      trackStock: newProduct.trackStock,
      stockQuantity: Number(newProduct.stockQuantity || 0),
      lowStockThreshold: Math.max(0, parseInt(String(newProduct.lowStockThreshold ?? 5), 10)),
      stockProductId: validStockIngredients.length > 0 ? undefined : newProduct.stockProductId || undefined,
      stockDeductQuantity: validStockIngredients.length > 0 ? undefined : Math.max(0.001, Number(newProduct.stockDeductQuantity || 1)),
      stockIngredients: validStockIngredients,
      recipeYield: Math.max(0.001, Number(newProduct.recipeYield || 1)),
      preparationMethod: newProduct.preparationMethod || '',
      stockPackageProductId: newProduct.stockPackageProductId || undefined,
      stockPackageSize: Math.max(0, Number(newProduct.stockPackageSize || 0)),
      stockUnit: newProduct.stockUnit || 'UN',
      internalUseOnly: newProduct.internalUseOnly === true,
      saleUnit: newProduct.saleUnit || 'UNIT',
      productionArea: newProduct.productionArea || 'AUTO',
      availableForDelivery: newProduct.internalUseOnly === true ? false : newProduct.availableForDelivery === true,
      modifierGroups: (newProduct.modifierGroups || []).filter(group => group.name && group.options.length)
    } as Product;

    if (editingId) updateProduct(productData);
    else addProduct(productData);

    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col flex-1 bg-[#eef4f8] font-sans">
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-[1360px] mx-auto pb-20 space-y-5">
          <header className="relative overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_30%)]"></div>
            <div className="relative grid gap-5 lg:grid-cols-[1fr_420px] items-center p-5 md:p-7">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-100">
                  <Sparkles className="h-3.5 w-3.5" /> Cardápio inteligente
                </div>
                <h1 className="mt-4 text-3xl md:text-5xl font-black tracking-tight">Cardápio organizado para vender mais rápido</h1>
                <p className="mt-3 max-w-2xl text-sm md:text-base text-white/72">Produtos em vitrine, categorias compactas, estoque visível e imagens prontas para deixar o PDV e o delivery mais profissionais.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-3">
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-white/55">Produtos</p><p className="mt-1 text-3xl font-black">{products.length}</p></div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-white/55">Categorias</p><p className="mt-1 text-3xl font-black">{categories.length}</p></div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-white/55">Delivery</p><p className="mt-1 text-3xl font-black text-emerald-200">{deliveryItems}</p></div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-white/55">Alertas</p><p className="mt-1 text-3xl font-black text-amber-200">{stockAlertItems}</p></div>
              </div>
            </div>
          </header>

          {isAddingCategory && (
            <div className="bg-white p-4 rounded-[1.5rem] shadow-sm border border-slate-200 flex items-center gap-2 animate-in fade-in slide-in-from-top-3 duration-300">
              <input type="text" placeholder="Nome da categoria..." className="flex-1 outline-none text-slate-700 p-3 text-sm font-bold bg-slate-50 rounded-xl border border-slate-200 focus:border-brand-500" value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} autoFocus onKeyDown={(event) => event.key === 'Enter' && handleSaveCategory()} />
              <Button onClick={handleSaveCategory} size="sm" className="rounded-xl h-11">Salvar</Button>
              <button onClick={() => setIsAddingCategory(false)} className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
          )}

          <nav className="flex w-full items-center gap-1 rounded-[1.5rem] border border-slate-200 bg-white p-1.5 shadow-sm" aria-label="Seção de produtos">
            <div className="relative flex-1">
              <button data-tour="products-tab" type="button" onClick={() => setActiveProductTab('products')} className={`w-full rounded-xl px-4 py-3 text-sm font-black transition ${activeProductTab === 'products' ? 'bg-slate-950 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}>Produtos</button>
              {activeProductTab === 'products' && <button type="button" onClick={startTour} aria-label="Abrir guia de Produtos" title="Guia de Produtos" className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-lg bg-white/10 px-2 py-1.5 text-[11px] font-black text-emerald-100 transition hover:bg-white/20"><CircleHelp className="h-4 w-4" /><span className="hidden sm:inline">Guia</span></button>}
            </div>
            <button type="button" onClick={() => setActiveProductTab('suppliers')} className={`flex-1 rounded-xl px-4 py-3 text-sm font-black transition ${activeProductTab === 'suppliers' ? 'bg-slate-950 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}>Fornecedores</button>
          </nav>

          {activeProductTab === 'products' && <section className="grid gap-5 lg:grid-cols-[260px_1fr] items-start">
            <aside data-tour="products-categories" className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4 lg:sticky lg:top-4">
              <div className="flex items-center justify-between mb-4">
                <div><p className="text-[10px] uppercase font-black tracking-[0.18em] text-slate-400">Navegação</p><h2 className="text-xl font-black text-slate-950">Categorias</h2></div>
                <button data-tour="products-add-category" onClick={() => setIsAddingCategory(true)} className="h-11 w-11 rounded-2xl bg-slate-950 text-white flex items-center justify-center shadow-lg shadow-slate-200 hover:bg-brand-600"><Plus className="h-5 w-5" /></button>
              </div>
              <div className="space-y-2">
                <button onClick={() => setSelectedCategory('Todos')} className={`relative w-full h-12 rounded-2xl pl-4 pr-14 flex items-center text-sm font-black transition-all ${selectedCategory === 'Todos' ? 'bg-slate-950 text-white shadow-lg shadow-slate-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}><span className="min-w-0 truncate text-left">Todos os produtos</span><span className="absolute right-4 w-8 text-right tabular-nums">{products.length}</span></button>
                {showTourExample && categories.length === 0 && <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-700">Exemplo <span className="float-right">1</span></div>}
                {categorySummaries.map(category => (
                  <div key={category.name} className={`group relative rounded-2xl transition-all ${selectedCategory === category.name ? 'bg-brand-50 ring-1 ring-brand-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <button onClick={() => setSelectedCategory(category.name)} className="h-12 w-full min-w-0 pl-4 pr-14 flex items-center text-left"><span className="min-w-0 truncate text-sm font-black text-slate-700">{category.name}</span><span className="absolute right-4 w-8 text-right text-xs font-black tabular-nums text-slate-400">{category.total}</span></button>
                    {category.name !== 'Geral' && <button onClick={() => handleDeleteCategory(category.name)} className="absolute right-12 top-1/2 -translate-y-1/2 h-8 w-8 rounded-xl text-slate-300 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all" title={`Excluir categoria ${category.name}`}><Trash2 className="mx-auto h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            </aside>

            <main className="min-w-0 space-y-4">
              <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4 md:p-5">
                <div className="grid gap-3 xl:grid-cols-[1fr_auto] xl:items-center">
                  <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
                    <div data-tour="products-search" className="h-12 rounded-2xl border border-slate-200 bg-slate-50 flex items-center px-4"><Search className="h-4 w-4 text-slate-400 mr-3 flex-shrink-0" /><input type="text" placeholder="Buscar por nome do produto..." className="w-full bg-transparent outline-none text-sm font-bold text-slate-700 placeholder:text-slate-400" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></div>
                    <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3"><p className="text-[9px] uppercase font-black tracking-widest text-slate-400">Visíveis</p><p className="text-lg font-black text-slate-950">{filteredProducts.length} produtos</p></div>
                    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3"><p className="text-[9px] uppercase font-black tracking-widest text-emerald-500">Delivery</p><p className="text-lg font-black text-emerald-700">{deliveryItems} ativos</p></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => setIsAddingCategory(true)} variant="outline" size="sm" className="h-12 rounded-2xl bg-white whitespace-nowrap"><Plus className="w-4 h-4" /> Categoria</Button>
                    <Button data-tour="products-new" onClick={() => handleOpenModal()} size="sm" className="h-12 rounded-2xl whitespace-nowrap"><Plus className="w-4 h-4" /> Novo Produto</Button>
                    <Button data-tour="products-catalog" onClick={openCatalog} variant="outline" size="sm" className="col-span-2 h-12 rounded-2xl border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 whitespace-nowrap"><PackagePlus className="w-4 h-4" /> Produtos pré-cadastrados</Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 flex items-center gap-3"><PackageCheck className="h-9 w-9 rounded-2xl bg-brand-50 p-2 text-brand-600" /><div><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Estoque</p><p className="text-xl font-black text-slate-950">{totalStockItems} controlados</p></div></div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 flex items-center gap-3"><Truck className="h-9 w-9 rounded-2xl bg-emerald-50 p-2 text-emerald-600" /><div><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Online</p><p className="text-xl font-black text-slate-950">{deliveryItems} no delivery</p></div></div>
                <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 flex items-center gap-3"><AlertTriangle className="h-9 w-9 rounded-2xl bg-amber-50 p-2 text-amber-600" /><div><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Atenção</p><p className="text-xl font-black text-slate-950">{stockAlertItems} alertas</p><p className="text-[10px] font-bold text-slate-400">{outOfStockItems} zerados • {lowStockItems} baixos</p></div></div>
              </div>

              {showTourExample ? (
                <article data-tour="products-card" className="max-w-sm overflow-hidden rounded-[2rem] border border-emerald-200 bg-white shadow-sm">
                  <div className="flex h-44 items-center justify-center bg-emerald-50"><ImageIcon className="h-12 w-12 text-emerald-300" /></div>
                  <div className="space-y-3 p-4"><span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Prévia do guia · Exemplo</span><h3 className="text-lg font-black text-slate-950">Produto de exemplo</h3><p className="text-2xl font-black text-brand-600">R$ 12,00</p><div className="grid grid-cols-2 gap-2"><span className="rounded-xl bg-slate-100 py-2 text-center text-xs font-black text-slate-500">Editar</span><span className="rounded-xl bg-red-50 py-2 text-center text-xs font-black text-red-400">Excluir</span></div></div>
                </article>
              ) : filteredProducts.length === 0 ? (
                <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-10 text-center"><ImageIcon className="mx-auto h-12 w-12 text-slate-300" /><h3 className="mt-4 text-xl font-black text-slate-950">Nenhum produto encontrado</h3><p className="mt-2 text-sm font-bold text-slate-500">Tente outra busca, outra categoria ou cadastre um novo produto.</p></div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {filteredProducts.map(product => (
                    <article key={product.id} data-tour={product.id === filteredProducts[0]?.id ? 'products-card' : undefined} className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-200">
                      <div className="relative h-44 bg-slate-50 border-b border-slate-100 flex items-center justify-center"><img src={product.image} alt={product.name} className="h-full w-full object-contain p-4" onError={(event) => (event.currentTarget.src = 'https://placehold.co/400x300?text=Produto')} /><span className="absolute left-4 top-4 max-w-[70%] truncate rounded-full bg-white/95 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-slate-600 shadow-sm">{product.category}</span></div>
                      <div className="p-4 space-y-4">
                        <div className="min-h-[74px]"><h3 className="line-clamp-2 text-lg font-black leading-tight text-slate-950">{product.name}</h3><p className="mt-1 text-2xl font-black text-brand-600">{formatCurrency(product.price)}</p></div>
                        <div className="flex flex-wrap gap-1.5 min-h-[26px]">{product.internalUseOnly && <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-black text-white">interno</span>}{product.trackStock && <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${Number(product.stockQuantity || 0) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{formatStockQuantity(product.stockQuantity, product.stockUnit || 'UN')} {STOCK_UNIT_LABELS[product.stockUnit || 'UN']}</span>}{product.availableForDelivery && <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-black text-blue-700">delivery</span>}{product.saleUnit === 'KG' && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-black text-amber-700">kg/balança</span>}</div>
                        <div className="grid grid-cols-2 gap-2 pt-1"><button onClick={() => handleOpenModal(product)} className="h-11 rounded-2xl bg-slate-100 text-sm font-black text-slate-700 hover:bg-brand-50 hover:text-brand-700 transition-colors flex items-center justify-center gap-2"><Edit2 className="h-4 w-4" /> Editar</button><button onClick={() => handleDeleteProduct(product)} className="h-11 rounded-2xl bg-red-50 text-sm font-black text-red-600 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"><Trash2 className="h-4 w-4" /> Excluir</button></div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </main>
          </section>
          }
          {activeProductTab === 'suppliers' && <SupplierPriceManager companyId={currentCompany?.id} products={products} />}
        </div>
      </div>
      {isCatalogOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:p-4">
          <div className="flex h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl animate-in zoom-in-95 duration-200 sm:h-auto sm:max-h-[92vh] sm:rounded-[2rem]">
            <div className="relative shrink-0 overflow-hidden bg-slate-950 px-4 py-4 text-white sm:p-6">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.28),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.18),transparent_36%)]" />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-200">Cadastro rápido</p>
                  <h2 className="mt-1 truncate text-xl font-black sm:text-2xl md:text-3xl">Produtos pré-cadastrados</h2>
                  <p className="mt-2 hidden max-w-2xl text-sm font-bold text-slate-300 sm:block">Escolha os produtos e informe apenas preço e estoque antes de adicionar ao seu catálogo.</p>
                </div>
                <button onClick={() => setIsCatalogOpen(false)} disabled={isCatalogSaving} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 sm:h-11 sm:w-11"><X className="h-5 w-5" /></button>
              </div>
              <div className="relative mt-3 flex gap-2 sm:mt-5">
                <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider ${catalogStep === 1 ? 'bg-emerald-300 text-slate-950' : 'bg-white/10 text-white'}`}>1. Selecionar</span>
                <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider ${catalogStep === 2 ? 'bg-emerald-300 text-slate-950' : 'bg-white/10 text-white'}`}>2. Configurar</span>
              </div>
            </div>

            <div ref={catalogScrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5 md:p-6">
              {catalogStep === 1 ? (
                <div className="space-y-4 sm:space-y-5">
                  <div className="grid gap-3 md:grid-cols-[1fr_240px]">
                    <div className="flex h-12 items-center rounded-2xl border border-slate-200 bg-slate-50 px-4">
                      <Search className="mr-3 h-4 w-4 text-slate-400" />
                      <input value={catalogSearch} onChange={event => { setCatalogSearch(event.target.value); setCatalogVisibleLimit(60); }} placeholder="Buscar nome exato, marca ou categoria..." className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400" autoFocus />
                    </div>
                    <select value={catalogCategory} onChange={event => { setCatalogCategory(event.target.value); setCatalogVisibleLimit(60); }} className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 outline-none focus:border-brand-400">
                      {catalogCategories.map(category => <option key={category} value={category}>{category}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] font-bold text-slate-500 sm:gap-2 sm:text-xs"><span>{filteredCatalogProducts.length.toLocaleString('pt-BR')} produtos encontrados</span><span className="hidden sm:inline">Imagens buscadas em lojas e CDNs comerciais.</span></div>
                  <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-3">
                    {visibleCatalogProducts.map(item => {
                      const alreadyAdded = normalizedProductNames.has(item.name.trim().toLocaleLowerCase('pt-BR'));
                      const selected = selectedCatalogIds.includes(item.id);
                      return (
                        <div key={item.id} data-catalog-product-id={item.id} role="button" tabIndex={alreadyAdded ? -1 : 0} aria-disabled={alreadyAdded} onClick={() => !alreadyAdded && toggleCatalogProduct(item)} onKeyDown={event => { if (!alreadyAdded && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); toggleCatalogProduct(item); } }} className={`relative min-h-[174px] rounded-[1.35rem] border p-3 text-left transition-all sm:min-h-[150px] sm:rounded-3xl sm:p-4 ${alreadyAdded ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-55' : selected ? 'cursor-pointer border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100' : 'cursor-pointer border-slate-200 bg-white hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-lg'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="relative h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-2xl border border-slate-100 bg-white sm:h-20 sm:w-20">
                            <img src={catalogImage(item)} alt={item.name} loading="lazy" onError={event => {
                              event.currentTarget.src = PLACEHOLDER_IMAGE;
                              setCatalogImages(current => {
                                if (!current[item.id]) return current;
                                const next = { ...current };
                                delete next[item.id];
                                localStorage.setItem(CATALOG_IMAGE_CACHE_KEY, JSON.stringify(next));
                                return next;
                              });
                              setCatalogImagesFailed(current => new Set(current).add(item.id));
                            }} className={`h-full w-full object-contain p-1 transition-opacity ${catalogImagesLoading.has(item.id) ? 'opacity-25' : 'opacity-100'}`} />
                            {catalogImagesLoading.has(item.id) && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-brand-600" /></div>}
                            {catalogImagesFailed.has(item.id) && !catalogImagesLoading.has(item.id) && <button type="button" title="Buscar imagem novamente" onClick={event => { event.stopPropagation(); loadCatalogProductImage(item, true); }} className="absolute inset-x-1 bottom-1 rounded-lg bg-slate-950/85 px-1 py-1 text-[8px] font-black uppercase text-white">Tentar imagem</button>}
                            </div>
                            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-xl sm:h-8 sm:w-8 ${selected ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`}>{selected ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}</span>
                          </div>
                          <div className="mt-3 min-w-0"><p className="line-clamp-2 text-sm font-black leading-tight text-slate-950">{item.name}</p><p className="mt-1 truncate text-[11px] font-bold text-slate-500">{item.brand || 'Produto comum'}</p></div>
                          <div className="mt-3 flex items-center justify-between gap-2"><span className="max-w-[70%] truncate rounded-full bg-slate-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-slate-600">{item.category}</span>{alreadyAdded && <span className="text-[9px] font-black uppercase text-emerald-600">Já cadastrado</span>}</div>
                        </div>
                      );
                    })}
                  </div>
                  {visibleCatalogProducts.length < filteredCatalogProducts.length && <Button type="button" variant="outline" onClick={() => setCatalogVisibleLimit(limit => limit + 60)} className="mx-auto flex h-11 rounded-2xl">Carregar mais 60 produtos</Button>}
                </div>
              ) : (
                <div className="space-y-3">
                  {PRE_CATALOG.filter(item => selectedCatalogIds.includes(item.id)).map(item => {
                    const draft = catalogDrafts[item.id] || { price: '', trackStock: true, stockQuantity: '0' };
                    return (
                      <div key={item.id} className="grid gap-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_180px_170px] md:items-end">
                        <div><p className="text-base font-black text-slate-950">{item.name}</p><p className="mt-1 text-xs font-bold text-slate-500">{item.category}{item.brand ? ` • ${item.brand}` : ''}</p></div>
                        <label><span className={LABEL_CLASS}>Preço de venda</span><input inputMode="decimal" value={draft.price} onChange={event => setCatalogDrafts(current => ({ ...current, [item.id]: { ...draft, price: event.target.value } }))} placeholder="0,00" className={FIELD_CLASS} /></label>
                        <div className="space-y-2">
                          <label className="flex h-11 cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-3"><span className="text-xs font-black text-slate-700">Controlar estoque</span><input type="checkbox" checked={draft.trackStock} onChange={event => setCatalogDrafts(current => ({ ...current, [item.id]: { ...draft, trackStock: event.target.checked } }))} className="h-4 w-4 accent-emerald-500" /></label>
                          {draft.trackStock && <input inputMode="decimal" value={draft.stockQuantity} onChange={event => setCatalogDrafts(current => ({ ...current, [item.id]: { ...draft, stockQuantity: event.target.value } }))} placeholder={`Quantidade (${STOCK_UNIT_LABELS[item.stockUnit || 'UN']})`} className={FIELD_CLASS} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 py-3 sm:p-5">
              <p className="hidden text-xs font-bold text-slate-500 sm:block">{selectedCatalogIds.length} produto(s) selecionado(s)</p>
              <div className="flex w-full gap-2 sm:w-auto">
                {catalogStep === 2 && <Button type="button" variant="outline" disabled={isCatalogSaving} onClick={() => setCatalogStep(1)} className="h-11 rounded-2xl"><ArrowLeft className="h-4 w-4" /> Voltar</Button>}
                <Button type="button" variant="outline" disabled={isCatalogSaving} onClick={() => setIsCatalogOpen(false)} className="h-11 flex-1 rounded-2xl sm:flex-none">Cancelar</Button>
                {catalogStep === 1 ? <Button type="button" disabled={selectedCatalogIds.length === 0} onClick={prepareCatalogDrafts} className="h-11 flex-[1.45] rounded-2xl sm:flex-none">Configurar <span className="hidden sm:inline">selecionados</span></Button> : <Button type="button" disabled={isCatalogSaving} onClick={saveCatalogProducts} className="h-11 flex-[1.45] rounded-2xl sm:flex-none">{isCatalogSaving ? 'Cadastrando...' : 'Adicionar produtos'}</Button>}
              </div>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="product-editor-title" className="bg-white rounded-[2rem] w-full max-w-5xl shadow-2xl relative animate-in zoom-in-95 duration-200 flex max-h-[95vh] flex-col overflow-hidden">
            <div className="relative shrink-0 overflow-hidden bg-slate-950 px-6 py-5 text-white flex items-center justify-between">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.26),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.2),transparent_34%)]"></div>
              <div>
                <p className="relative text-[10px] font-black uppercase tracking-[0.28em] text-emerald-200">Produtos</p>
                <h2 id="product-editor-title" className="relative text-2xl md:text-3xl font-black">{editingId ? 'Editar produto' : 'Novo produto'}</h2>
                <p className="relative text-xs text-white/65 font-bold mt-1">{newProduct.name || 'Organize os dados em três etapas simples.'}</p>
              </div>
              <div className="relative flex items-center gap-2">
                {!tourOpen && <button type="button" onClick={startTour} aria-label="Abrir guia de Produtos" title="Guia de Produtos" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><CircleHelp className="h-5 w-5" /></button>}
                <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-red-500/20 hover:text-red-100"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <nav role="tablist" aria-label="Seções do produto" className="grid shrink-0 grid-cols-3 gap-1 border-b border-slate-100 bg-white px-4 py-3 sm:gap-2 sm:px-6">
                {([
                  ['basic', 'Dados e imagem', ClipboardList],
                  ['stock', 'Estoque e canais', Boxes],
                  ['advanced', 'Preços e operação', DollarSign]
                ] as const).map(([tab, label, Icon]) => (
                  <button key={tab} data-tour={tab === 'stock' ? 'products-editor-stock' : undefined} type="button" role="tab" aria-selected={productEditorTab === tab} onClick={() => { setProductEditorTab(tab); productFormScrollRef.current?.scrollTo?.(0, 0); }} className={`flex min-h-11 items-center justify-center gap-2 rounded-xl px-2 text-[11px] font-black transition sm:text-sm ${productEditorTab === tab ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>
                    <Icon className="hidden h-4 w-4 shrink-0 sm:block" /> {label}
                  </button>
                ))}
              </nav>
              <div ref={productFormScrollRef} className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid gap-5 lg:grid-cols-[250px_1fr]">
                <aside className="space-y-4">
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3">
                    <label className={LABEL_CLASS}>Pré-visualização</label>
                    <div className="w-full h-40 bg-white rounded-2xl border border-slate-200 flex items-center justify-center overflow-hidden relative">
                  {newProduct.image ? (
                    <img src={newProduct.image} alt="Preview" className="w-full h-full object-contain p-4 animate-in fade-in duration-700" onError={(event) => (event.currentTarget.src = 'https://placehold.co/400x300?text=Erro+na+Imagem')} />
                  ) : (
                    <div className="text-center p-6">
                      <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2 opacity-70" />
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Digite o nome e use Auto buscar</p>
                    </div>
                  )}
                    </div>
                  </div>

                  <div data-tour="products-editor-availability" className="rounded-[1.5rem] border border-slate-200 bg-white p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <p className="text-sm font-black text-slate-900">Disponibilidade</p>
                    </div>
                    {([
                      ['active', 'Produto ativo', 'Disponível no cadastro.'],
                      ['allowSale', 'Vender no PDV', 'Permite lançar na comanda.']
                    ] as const).map(([field, title, hint]) => (
                      <label key={field} className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2">
                        <span>
                          <span className="block text-xs font-black text-slate-800">{title}</span>
                          <span className="block text-[10px] font-bold text-slate-400">{hint}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={newProduct[field] !== false}
                          onChange={event => setNewProduct({ ...newProduct, [field]: event.target.checked })}
                          className="h-5 w-5 rounded border-slate-300 accent-emerald-600"
                        />
                      </label>
                    ))}
                    <details className="rounded-xl border border-slate-100 bg-slate-50 p-2.5">
                      <summary className="cursor-pointer text-xs font-black text-slate-600">Características do produto</summary>
                      <div className="mt-2 space-y-2">
                        {([
                          ['composition', 'Produto composto', 'Classificação; ingredientes ficam em Estoque.'],
                          ['manufactured', 'Fabricado', 'Produzido pela casa.'],
                          ['fractioned', 'Fracionado', 'Controlado em partes.']
                        ] as const).map(([field, title, hint]) => (
                          <label key={field} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                            <span><span className="block text-xs font-black text-slate-800">{title}</span><span className="block text-[10px] text-slate-400">{hint}</span></span>
                            <input type="checkbox" checked={newProduct[field] === true} onChange={event => setNewProduct({ ...newProduct, [field]: event.target.checked })} className="h-5 w-5 rounded border-slate-300 accent-emerald-600" />
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                </aside>

                <section role="tabpanel" className="space-y-5">
                  {productEditorTab === 'basic' && (
                  <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-slate-900" />
                      <div>
                        <h3 data-tour="products-editor-basic" className="text-lg font-black text-slate-900">Dados básicos</h3>
                        <p className="text-xs font-bold text-slate-400">Identificação, categoria, códigos e localização.</p>
                      </div>
                    </div>

              <div>
                <label className={LABEL_CLASS}>Nome do Produto</label>
                <input required type="text" className={`${FIELD_CLASS} text-lg`} value={newProduct.name} onChange={event => setNewProduct({ ...newProduct, name: event.target.value })} placeholder="Ex: Brahma 600" />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>Preço de venda</label>
                  <input required type="number" step="0.01" className={`${FIELD_CLASS} font-black text-brand-600`} value={newProduct.price} onChange={event => setNewProduct({ ...newProduct, price: parseFloat(event.target.value) })} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Categoria</label>
                  <select className={FIELD_CLASS} value={newProduct.category} onChange={event => setNewProduct({ ...newProduct, category: event.target.value })}>
                    {tourOpen && newProduct.category && !categories.includes(newProduct.category) && <option>{newProduct.category}</option>}
                    {categories.map(category => <option key={category}>{category}</option>)}
                  </select>
                </div>
              </div>

              <details className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <summary className="cursor-pointer text-sm font-black text-slate-700">Adicionais e escolhas do restaurante</summary>
                <p className="mt-1 text-xs text-slate-500">Ex.: tamanho, ponto da carne, acompanhamentos e opcionais. Cada grupo controla o mínimo, máximo e preço.</p>
                <div className="mt-4 space-y-4">
                  {(newProduct.modifierGroups || []).map((group, groupIndex) => (
                    <div key={group.id} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                      <div className="flex gap-2"><input className={`${FIELD_CLASS} flex-1`} value={group.name} onChange={event => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, name: event.target.value } : item) }))} placeholder="Ex.: Ponto da carne" /><button type="button" onClick={() => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).filter((_, index) => index !== groupIndex) }))} className="rounded-xl px-3 text-xs font-black text-red-600 bg-red-50">Remover</button></div>
                      <div className="grid grid-cols-2 gap-3"><label><span className={LABEL_CLASS}>Mínimo</span><input type="number" min="0" className={FIELD_CLASS} value={group.minSelections} onChange={event => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, minSelections: Math.max(0, Number(event.target.value || 0)) } : item) }))} /></label><label><span className={LABEL_CLASS}>Máximo</span><input type="number" min="1" className={FIELD_CLASS} value={group.maxSelections} onChange={event => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, maxSelections: Math.max(1, Number(event.target.value || 1)) } : item) }))} /></label></div>
                      <div className="space-y-2">{group.options.map((option, optionIndex) => <div key={option.id} className="grid grid-cols-[1fr_100px_auto] gap-2"><input className={FIELD_CLASS} value={option.name} onChange={event => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, options: item.options.map((choice, choiceIndex) => choiceIndex === optionIndex ? { ...choice, name: event.target.value } : choice) } : item) }))} placeholder="Opção" /><input type="number" min="0" step="0.01" className={FIELD_CLASS} value={option.price} onChange={event => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, options: item.options.map((choice, choiceIndex) => choiceIndex === optionIndex ? { ...choice, price: Number(event.target.value || 0) } : choice) } : item) }))} /><button type="button" onClick={() => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, options: item.options.filter((_, choiceIndex) => choiceIndex !== optionIndex) } : item) }))} className="px-2 text-xs font-black text-red-600">×</button></div>)}<button type="button" onClick={() => setNewProduct(current => ({ ...current, modifierGroups: (current.modifierGroups || []).map((item, index) => index === groupIndex ? { ...item, options: [...item.options, { id: crypto.randomUUID(), name: '', price: 0 }] } : item) }))} className="text-xs font-black text-brand-600">+ Adicionar opção</button></div>
                    </div>
                  ))}
                  <button type="button" onClick={() => setNewProduct(current => ({ ...current, modifierGroups: [...(current.modifierGroups || []), { id: crypto.randomUUID(), name: '', minSelections: 0, maxSelections: 1, options: [{ id: crypto.randomUUID(), name: '', price: 0 }] } as ProductModifierGroup] }))} className="rounded-xl border border-dashed border-brand-300 bg-brand-50 px-4 py-3 text-sm font-black text-brand-700">+ Criar grupo de escolhas</button>
                </div>
              </details>

              <details className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <summary className="cursor-pointer text-sm font-black text-slate-700">Códigos e identificação adicional</summary>
                <p className="mt-1 text-xs text-slate-500">Código de barras, referência, marca e dados fiscais são opcionais.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className={LABEL_CLASS}>Código de barras</label>
                  <div className="relative">
                    <Barcode className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input type="text" className={`${FIELD_CLASS} pl-10`} value={newProduct.barcode || ''} onChange={event => setNewProduct({ ...newProduct, barcode: event.target.value })} placeholder="EAN / GTIN" />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Referência / SKU</label>
                  <input type="text" className={FIELD_CLASS} value={newProduct.sku || ''} onChange={event => setNewProduct({ ...newProduct, sku: event.target.value })} placeholder="Código interno" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Marca</label>
                  <input type="text" className={FIELD_CLASS} value={newProduct.brand || ''} onChange={event => setNewProduct({ ...newProduct, brand: event.target.value })} placeholder="Ex: Ambev" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>NCM</label>
                  <input type="text" inputMode="numeric" className={FIELD_CLASS} value={newProduct.ncm || ''} onChange={event => setNewProduct({ ...newProduct, ncm: event.target.value })} placeholder="Ex: 22030000" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>CFOP</label>
                  <input type="text" inputMode="numeric" className={FIELD_CLASS} value={newProduct.cfop || ''} onChange={event => setNewProduct({ ...newProduct, cfop: event.target.value })} placeholder="Ex: 5102" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Localização</label>
                  <div className="relative">
                    <Warehouse className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input type="text" className={`${FIELD_CLASS} pl-10`} value={newProduct.location || ''} onChange={event => setNewProduct({ ...newProduct, location: event.target.value })} placeholder="Prateleira / câmara" />
                  </div>
                </div>
              </div>

              </details>
                  </div>
                  )}

                  {productEditorTab === 'advanced' && (<>
                  <div data-tour="products-editor-pricing" className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                      <div>
                        <h3 className="text-lg font-black text-slate-900">Precificação e atacado</h3>
                        <p className="text-xs font-bold text-slate-400">Controle preço, custo, margem e venda em quantidade.</p>
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      <div>
                        <label className={LABEL_CLASS}>Compra (nota)</label>
                        <input type="number" step="0.01" className={FIELD_CLASS} value={newProduct.purchasePrice || 0} onChange={event => setNewProduct({ ...newProduct, purchasePrice: Number(event.target.value || 0) })} />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Custo total</label>
                        <input type="number" step="0.01" className={FIELD_CLASS} value={newProduct.costPrice || 0} onChange={event => setNewProduct({ ...newProduct, costPrice: Number(event.target.value || 0) })} />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Lucro %</label>
                        <div className="relative">
                          <Percent className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input type="number" step="0.01" className={`${FIELD_CLASS} pl-10`} value={newProduct.profitMargin || 0} onChange={event => setNewProduct({ ...newProduct, profitMargin: Number(event.target.value || 0) })} />
                        </div>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Qtd. atacado</label>
                        <input type="number" step="0.001" className={FIELD_CLASS} value={newProduct.wholesaleQuantity || 0} onChange={event => setNewProduct({ ...newProduct, wholesaleQuantity: Number(event.target.value || 0) })} />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Preço atacado</label>
                        <input type="number" step="0.01" className={FIELD_CLASS} value={newProduct.wholesalePrice || 0} onChange={event => setNewProduct({ ...newProduct, wholesalePrice: Number(event.target.value || 0) })} />
                      </div>
                    </div>
                  </div>

                  <div data-tour="products-editor-operation" className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <Scale className="h-5 w-5 text-blue-600" />
                      <div>
                        <h3 className="text-lg font-black text-slate-900">Operação e medidas</h3>
                        <p className="text-xs font-bold text-slate-400">Destino de preparo, balança, pesos e validade.</p>
                      </div>
                    </div>

              <div className={`grid gap-4 ${currentCompany?.useScale ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Vai para</label>
                  <select
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-xs font-bold"
                    value={newProduct.productionArea || 'AUTO'}
                    onChange={event => setNewProduct({ ...newProduct, productionArea: event.target.value as Product['productionArea'] })}
                  >
                    <option value="AUTO">Automático</option>
                    <option value="KITCHEN">Cozinha</option>
                    <option value="BAR">Bar / bebida pronta</option>
                    <option value="BOTH">Cozinha e bar</option>
                  </select>
                  <p className="text-[11px] text-slate-400 mt-2">Bebidas prontas ficam fora da cozinha no automático.</p>
                </div>

                {currentCompany?.useScale && (
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Unidade de venda (PDV)</label>
                    <select
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-xs font-bold"
                      value={newProduct.saleUnit || 'UNIT'}
                      onChange={event => setNewProduct({
                        ...newProduct,
                        saleUnit: event.target.value as Product['saleUnit'],
                        weighable: event.target.value === 'KG'
                      })}
                    >
                      <option value="UNIT">Unidade</option>
                      <option value="KG">Kg / balança</option>
                    </select>
                    <p className="text-[11px] text-slate-400 mt-2">Produto em Kg pede o peso antes de lançar no PDV.</p>
                  </div>
                )}
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div>
                  <label className={LABEL_CLASS}>Peso bruto (kg)</label>
                  <input type="number" step="0.001" className={FIELD_CLASS} value={newProduct.grossWeight || 0} onChange={event => setNewProduct({ ...newProduct, grossWeight: Number(event.target.value || 0) })} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Peso líquido (kg)</label>
                  <input type="number" step="0.001" className={FIELD_CLASS} value={newProduct.netWeight || 0} onChange={event => setNewProduct({ ...newProduct, netWeight: Number(event.target.value || 0) })} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Data de validade</label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input type="date" className={`${FIELD_CLASS} pl-10`} value={newProduct.expirationDate || ''} onChange={event => setNewProduct({ ...newProduct, expirationDate: event.target.value })} />
                  </div>
                </div>
              </div>
                  </div>
                  </>)}

              {productEditorTab === 'basic' && (
              <div data-tour="products-editor-image">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Imagem do Produto</label>
                <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2">
                  <input type="text" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-xs text-slate-500" value={newProduct.image?.startsWith('data:') ? '' : (newProduct.image || '')} onChange={event => setNewProduct({ ...newProduct, image: event.target.value })} placeholder={newProduct.image?.startsWith('data:') ? 'Imagem atual incorporada — cole outra URL para trocar' : 'Cole a URL ou use Auto buscar...'} />
                  <button type="button" onClick={handleImageAutoSearch} disabled={!newProduct.name || isImageLoading} className="px-4 h-12 bg-slate-900 text-white rounded-xl hover:bg-brand-600 flex items-center justify-center gap-2 text-[10px] font-black transition-all shadow-lg shadow-slate-200 disabled:bg-slate-300 disabled:shadow-none">
                    {isImageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} AUTO BUSCAR
                  </button>
                  <button type="button" onClick={handleImageSearch} disabled={!newProduct.name} className="px-4 h-12 bg-brand-50 text-brand-700 rounded-xl hover:bg-brand-100 flex items-center justify-center gap-2 text-[10px] font-black transition-all disabled:bg-slate-100 disabled:text-slate-400">
                    <ExternalLink className="w-4 h-4" /> GOOGLE
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Auto buscar procura uma imagem comercial do produto. Se não encontrar, use o Google e cole a URL da loja ou CDN.</p>
              </div>
              )}

              {productEditorTab === 'stock' && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <div>
                    <label htmlFor="internal-use-only" className="text-sm font-black text-slate-700">Item interno / insumo</label>
                    <p className="text-[11px] text-slate-400 font-bold mt-0.5">Não aparece no PDV nem no delivery. Use para estoque pai: calabresa, carne, vodka, polpa, xarope e outros insumos.</p>
                  </div>
                  <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                    <input
                      type="checkbox"
                      name="internal-use-only"
                      id="internal-use-only"
                      checked={newProduct.internalUseOnly === true}
                      onChange={(event) => setNewProduct({
                        ...newProduct,
                        internalUseOnly: event.target.checked,
                        trackStock: event.target.checked ? true : newProduct.trackStock,
                        availableForDelivery: event.target.checked ? false : newProduct.availableForDelivery
                      })}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label htmlFor="internal-use-only" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>

                {currentCompany?.useDelivery === true && (
                  <>
                    <div className={`flex items-center justify-between ${newProduct.internalUseOnly ? 'opacity-45 pointer-events-none' : ''}`}>
                      <div>
                        <label htmlFor="available-delivery" className="text-sm font-black text-slate-700">Vender no Delivery</label>
                        <p className="text-[11px] text-slate-400 font-bold mt-0.5">Só produtos marcados aparecem no cardápio online.</p>
                      </div>
                      <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                        <input type="checkbox" name="available-delivery" id="available-delivery" checked={newProduct.availableForDelivery === true} onChange={(event) => setNewProduct({ ...newProduct, availableForDelivery: event.target.checked })} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                        <label htmlFor="available-delivery" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                      </div>
                    </div>

                    <div className="border-t border-slate-200"></div>
                  </>
                )}

                <div data-tour="products-editor-tracking" className="flex items-center justify-between">
                  <label htmlFor="track-stock" className="text-sm font-black text-slate-700 flex items-center gap-2"><Boxes className="w-4 h-4" /> Controlar Estoque</label>
                  <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                    <input type="checkbox" name="track-stock" id="track-stock" checked={newProduct.trackStock} onChange={(event) => setNewProduct({ ...newProduct, trackStock: event.target.checked })} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                    <label htmlFor="track-stock" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                {newProduct.trackStock && (
                  <div className="animate-in fade-in duration-300 space-y-3">
                    <div className="grid sm:grid-cols-[1fr_140px] gap-3">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Estoque atual</label>
                        <input type="number" min="0" step="0.001" className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none" value={newProduct.stockQuantity} onChange={event => setNewProduct({ ...newProduct, stockQuantity: Number(event.target.value || 0) })} />
                        <p className="text-[11px] text-slate-400 mt-2">Quantidade disponível. Ex: 3 kg, 2500 g, 1500 ml, 12 un.</p>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Unidade no estoque</label>
                        <select
                          className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none text-xs font-bold"
                          value={newProduct.stockUnit || 'UN'}
                          onChange={event => setNewProduct({ ...newProduct, stockUnit: event.target.value as Product['stockUnit'] })}
                        >
                          <option value="UN">Unidade</option>
                          <option value="G">Gramas (g)</option>
                          <option value="KG">Quilos (kg)</option>
                          <option value="ML">Mililitros (ml)</option>
                          <option value="L">Litros (L)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Avisar estoque baixo com</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none"
                        value={newProduct.lowStockThreshold ?? 5}
                        onChange={event => setNewProduct({ ...newProduct, lowStockThreshold: parseInt(event.target.value || '0', 10) })}
                      />
                      <p className="text-[11px] text-slate-400 mt-2">Ex: se para este item 12 unidades já é pouco, coloque 12. Zero desativa o alerta baixo.</p>
                    </div>

                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 space-y-3">
                      <div>
                        <p className="text-xs font-black text-emerald-900">Abrir embalagem automaticamente</p>
                        <p className="text-[11px] text-emerald-700 mt-1">Use quando este produto é vendido picado, mas chega em caixa, fardo, pacote ou carteira.</p>
                      </div>

                      <div>
                        <label className="block text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-1">Produto embalagem / caixa / pacote</label>
                        <select
                          className="w-full p-3 bg-white border border-emerald-100 rounded-xl outline-none text-xs font-bold"
                          value={newProduct.stockPackageProductId || ''}
                          onChange={event => setNewProduct({ ...newProduct, stockPackageProductId: event.target.value || '', stockPackageSize: event.target.value ? (newProduct.stockPackageSize || 1) : 0 })}
                        >
                          <option value="">Não abrir embalagem automaticamente</option>
                          {stockSourceProducts.map(product => (
                            <option key={product.id} value={product.id}>{product.name} ({formatStockQuantity(product.stockQuantity, product.stockUnit || 'UN')} em estoque)</option>
                          ))}
                        </select>
                        <p className="text-[11px] text-emerald-700 mt-2">Ex: o produto “Cigarro unidade” abre a embalagem “Carteira Marlboro”.</p>
                      </div>

                      {newProduct.stockPackageProductId && (
                        <div>
                          <label className="block text-[10px] font-black text-emerald-700 uppercase tracking-widest mb-1">Unidades dentro de cada embalagem</label>
                          <input
                            type="number"
                            min="1"
                            step="0.001"
                            className="w-full p-3 bg-white border border-emerald-100 rounded-xl outline-none"
                            value={newProduct.stockPackageSize || 1}
                            onChange={event => setNewProduct({ ...newProduct, stockPackageSize: Number(event.target.value || 1) })}
                          />
                          <p className="text-[11px] text-emerald-700 mt-2">Ex: se uma carteira tem 12 cigarros, coloque 12. Quando o solto acabar, baixa 1 carteira e adiciona 12 unidades soltas.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div data-tour="products-editor-ingredients" className="border-t border-slate-200 pt-3 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black text-slate-800">Ficha técnica / baixa de insumos</p>
                      <p className="text-[11px] text-slate-400 mt-1">Adicione todos os itens de estoque consumidos quando este produto for vendido.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewProduct({
                        ...newProduct,
                        stockProductId: '',
                        stockDeductQuantity: 1,
                        stockIngredients: [...(newProduct.stockIngredients || []), { productId: stockSourceProducts[0]?.id || '', quantity: 1, unit: stockSourceProducts[0]?.stockUnit || 'UN' }]
                      })}
                      className="px-3 py-2 rounded-xl bg-slate-900 text-white text-[10px] font-black hover:bg-brand-600 whitespace-nowrap"
                    >
                      + Insumo
                    </button>
                  </div>

                  {(newProduct.stockIngredients || []).length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-[11px] font-bold text-slate-400">
                      Nenhum insumo vinculado. Se for porção, drink ou preparo de cozinha, adicione os ingredientes aqui.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(newProduct.stockIngredients || []).map((ingredient, index) => {
                        const selectedStockProduct = stockSourceProducts.find(product => product.id === ingredient.productId);
                        const unitLabel = STOCK_UNIT_LABELS[ingredient.unit || selectedStockProduct?.stockUnit || 'UN'];
                        const compatibleUnits = selectedStockProduct?.stockUnit === 'KG' || selectedStockProduct?.stockUnit === 'G'
                          ? ['G', 'KG'] as const
                          : selectedStockProduct?.stockUnit === 'ML' || selectedStockProduct?.stockUnit === 'L'
                            ? ['ML', 'L'] as const
                            : ['UN'] as const;
                        return (
                          <div key={`${ingredient.productId}-${index}`} className="grid gap-2 sm:grid-cols-[1fr_120px_90px_40px] items-end rounded-2xl border border-slate-200 bg-white p-3">
                            <div>
                              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Insumo / item pai</label>
                              <select
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-xs font-bold"
                                value={ingredient.productId}
                                onChange={event => {
                                  const stockIngredients = [...(newProduct.stockIngredients || [])];
                                  const source = stockSourceProducts.find(product => product.id === event.target.value);
                                  stockIngredients[index] = { ...stockIngredients[index], productId: event.target.value, unit: source?.stockUnit || 'UN' };
                                  setNewProduct({ ...newProduct, stockIngredients });
                                }}
                              >
                                <option value="">Selecione o insumo</option>
                                {stockSourceProducts.map(product => (
                                  <option key={product.id} value={product.id}>{product.name} ({formatStockQuantity(product.stockQuantity, product.stockUnit || 'UN')} {STOCK_UNIT_LABELS[product.stockUnit || 'UN']})</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Unidade</label>
                              <select
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-xs font-bold"
                                value={ingredient.unit || selectedStockProduct?.stockUnit || 'UN'}
                                onChange={event => {
                                  const stockIngredients = [...(newProduct.stockIngredients || [])];
                                  stockIngredients[index] = { ...stockIngredients[index], unit: event.target.value as NonNullable<Product['stockUnit']> };
                                  setNewProduct({ ...newProduct, stockIngredients });
                                }}
                              >
                                {compatibleUnits.map(unit => <option key={unit} value={unit}>{STOCK_UNIT_LABELS[unit]}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Consumo</label>
                              <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                                <input
                                  type="number"
                                  min="0.001"
                                  step="0.001"
                                  className="w-full p-3 bg-transparent outline-none text-sm font-black"
                                  value={ingredient.quantity}
                                  onChange={event => {
                                    const stockIngredients = [...(newProduct.stockIngredients || [])];
                                    stockIngredients[index] = { ...stockIngredients[index], quantity: Number(event.target.value || 1) };
                                    setNewProduct({ ...newProduct, stockIngredients });
                                  }}
                                />
                                <span className="pr-3 text-[10px] font-black text-slate-400">{unitLabel}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setNewProduct({ ...newProduct, stockIngredients: (newProduct.stockIngredients || []).filter((_, itemIndex) => itemIndex !== index) })}
                              className="h-10 rounded-xl bg-red-50 text-red-500 hover:bg-red-100"
                              title="Remover insumo"
                            >
                              <Trash2 className="w-4 h-4 mx-auto" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(newProduct.stockIngredients || []).length > 0 && (
                    <div className="grid gap-3 rounded-2xl bg-brand-50 p-4 sm:grid-cols-2">
                      <label className="text-xs font-black text-brand-900">Rendimento da receita
                        <input type="number" min="0.001" step="0.001" value={newProduct.recipeYield || 1} onChange={event => setNewProduct({ ...newProduct, recipeYield: Number(event.target.value || 1) })} className="mt-2 w-full rounded-xl border border-brand-100 bg-white p-3 text-sm" />
                        <span className="mt-1 block text-[10px] font-bold text-brand-700">Porções produzidas pela ficha. O consumo é dividido por este valor.</span>
                      </label>
                      <label className="text-xs font-black text-brand-900">Modo de preparo
                        <textarea value={newProduct.preparationMethod || ''} onChange={event => setNewProduct({ ...newProduct, preparationMethod: event.target.value })} maxLength={1200} rows={3} className="mt-2 w-full rounded-xl border border-brand-100 bg-white p-3 text-sm" placeholder="Ex.: grelhar e finalizar com molho." />
                      </label>
                      <p className="sm:col-span-2 text-xs font-black text-brand-800">Custo estimado por porção: {formatCurrency(estimatedRecipeCost)}</p>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400">Ex: receita com 200 g de carne e rendimento 2 baixa 100 g por venda. O sistema converte g/kg e ml/L automaticamente.</p>
                </div>
              </div>
              )}

                </section>
              </div>
              </div>

              <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-3">
                <button type="button" className="rounded-xl px-4 py-3 text-slate-500 font-bold text-sm" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                <Button data-tour="products-editor-save" type="submit" disabled={tourOpen} className="rounded-xl h-11 px-6 disabled:opacity-60"><Tags className="w-4 h-4" /> {editingId ? 'Salvar alterações' : 'Criar produto'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {tourOpen && <SpotlightTour label="Guia de Produtos" steps={currentTourSteps} stepIndex={tourStep} onStepChange={changeTourStep} onFinish={finishTour} />}
    </div>
  );
};


